import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, KpiCard, Loading, Empty, PageHeader, ExportBar, exportExcel, CHART_COLORS, ScoreBadge, RiskBadge } from "./_shared";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Gauge, TrendingUp, Award, Clock, CheckCircle2, Star } from "lucide-react";

const tip = { contentStyle: { borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 } };
const LABEL = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function ExecutiveDashboard() {
  const { query } = useFilters();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/executive${query}`).then((r) => setD(r.data)).finally(() => setLoading(false));
  }, [query]);

  if (loading) return <Loading />;
  if (!d || d.kpis.total_participants === 0) return <Empty />;
  const k = d.kpis;

  const eiData = Object.entries(d.emotional_intelligence).map(([key, value]) => ({ metric: LABEL(key), value }));
  const commData = Object.entries(d.communication).map(([key, value]) => ({ metric: LABEL(key), value }));

  const handleExcel = () =>
    exportExcel([
      { name: "Exec KPIs", rows: [k] },
      { name: "Dept Readiness", rows: d.department_readiness },
      { name: "Role Readiness", rows: d.role_readiness },
      { name: "Competencies", rows: d.competency_overview },
    ], "executive-dashboard.xlsx");

  return (
    <div data-testid="executive-page">
      <PageHeader title="Executive Dashboard"
        description="Program effectiveness at a glance for HR and senior leadership — readiness, behaviour trends and risk."
        right={<ExportBar onExcel={handleExcel} />} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <KpiCard testid="exec-kpi-completion" label="Completion Rate" value={`${k.completion_rate}%`} icon={CheckCircle2} accent="#0f766e" />
        <KpiCard label="Avg Competency Gain" value={`+${k.avg_competency_gain}`} icon={TrendingUp} accent="#4d7c0f" />
        <KpiCard testid="exec-kpi-cert" label="Cert Readiness" value={`${k.certification_readiness}%`} icon={Award} />
        <KpiCard label="Avg Practice Hrs" value={`${k.avg_practice_hours}h`} icon={Clock} accent="#0369a1" />
        <KpiCard label="Avg Score" value={k.avg_score} icon={Star} accent="#1d4ed8" />
        <KpiCard label="Participants" value={k.total_participants} icon={Gauge} accent="#be185d" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Panel title="Learning Curve" subtitle="Improvement across the program" className="lg:col-span-2" testid="exec-learning-curve">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.learning_curve}>
              <defs>
                <linearGradient id="lc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f766e" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0f766e" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#a8a29e" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#a8a29e" />
              <Tooltip {...tip} />
              <Area type="monotone" dataKey="value" stroke="#0f766e" strokeWidth={2.5} fill="url(#lc)" name="Avg score" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Competency Overview" testid="exec-competency">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={d.competency_overview} outerRadius="62%" margin={{ top: 8, right: 60, bottom: 8, left: 60 }}>
              <PolarGrid stroke="#e7e5e4" />
              <PolarAngleAxis dataKey="competency" tick={{ fontSize: 9, fill: "#57534e" }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8 }} stroke="#d6d3d1" />
              <Radar dataKey="score" stroke="#b81f24" fill="#b81f24" fillOpacity={0.22} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Panel title="Department Readiness" subtitle="Average score & readiness by business unit" testid="exec-dept">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.department_readiness} margin={{ bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
              <XAxis dataKey="department" tick={{ fontSize: 11 }} stroke="#a8a29e" interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#a8a29e" />
              <Tooltip {...tip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="avg_score" name="Avg score" fill="#b81f24" radius={[6, 6, 0, 0]} />
              <Bar dataKey="readiness" name="Readiness" fill="#0f766e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Role-wise Readiness" testid="exec-role">
          <div className="space-y-3 mt-1">
            {d.role_readiness.map((r) => (
              <div key={r.role}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-stone-700">{r.role} <span className="text-stone-400 text-xs">({r.count})</span></span>
                  <span className="font-semibold tabular-nums">{r.readiness}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#b81f24] to-[#d4575b]" style={{ width: `${r.readiness}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-stone-100">
            <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Most Improved Teams</div>
            <div className="flex flex-wrap gap-2">
              {d.most_improved_teams.map((t) => (
                <span key={t.team} className="text-xs bg-emerald-50 text-emerald-700 rounded-md px-2.5 py-1 font-medium">{t.team} +{t.improvement}</span>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Panel title="Emotional Intelligence" subtitle="Empathy, respect, curiosity, psychological safety…" testid="exec-ei">
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={eiData} outerRadius="70%">
              <PolarGrid stroke="#e7e5e4" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 9, fill: "#57534e" }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8 }} stroke="#d6d3d1" />
              <Radar dataKey="value" stroke="#be185d" fill="#be185d" fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-stone-400 mt-2">Note: lower Defensiveness is better.</p>
        </Panel>

        <Panel title="Communication Analytics" subtitle="Clarity, confidence, tone & language" testid="exec-comm">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={commData} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#a8a29e" />
              <YAxis type="category" dataKey="metric" width={120} tick={{ fontSize: 10 }} stroke="#78716c" />
              <Tooltip {...tip} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#1d4ed8" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Coaching Style Distribution" subtitle="How learners show up in conversations" testid="exec-styles">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={d.coaching_styles.map((s) => ({ name: s.style, count: s.count }))} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={({ name }) => name}>
                {d.coaching_styles.map((s, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip {...tip} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Risk Flags" subtitle="Learners who may need facilitator intervention" testid="exec-risk">
          <div className="flex flex-wrap gap-2 mb-4">
            {d.risk_flags.length === 0 && <span className="text-sm text-stone-400">No risks flagged.</span>}
            {d.risk_flags.map((r) => (
              <div key={r.flag} className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
                <RiskBadge flag={r.flag} /><span className="text-sm font-semibold text-[#b81f24] tabular-nums">{r.count}</span>
              </div>
            ))}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1.5">
            {d.at_risk_participants.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-stone-100 pb-1.5">
                <div>
                  <span className="text-stone-800">{p.name}</span>
                  <span className="text-xs text-stone-400 ml-2">{p.department || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ScoreBadge value={p.score} />
                  <span className="text-[11px] text-stone-400">{p.flags.length} flag(s)</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
